import { Box, Card, CardContent, SxProps, Theme, Typography } from "@mui/material";
import { ReactNode } from "react";
import { bkdp } from "../theme";

type AppCardProps = {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  action?: ReactNode;
  /** SHL-2: the icon tile sits in the eyebrow row, left of the eyebrow, with the action at the right. Use AppIconTile. */
  icon?: ReactNode;
  children?: ReactNode;
  featured?: boolean;
  sx?: SxProps<Theme>;
};

/**
 * SHL-2: one object, not three stacked blocks. The icon shares the eyebrow row; the internal margins are one value
 * (GAP) from the eyebrow row to the title, the title to the subtitle, and the subtitle to the children; the padding is
 * px 3, py 2.5. The card fills its grid cell (height 100%) so cards in a row share a height and the longest subtitle
 * sets it; a card alone in a row is the only thing that sets the row, so it never stretches over dead space.
 */
const GAP = 1.25;

export function AppCard({
  eyebrow,
  title,
  subtitle,
  action,
  icon,
  children,
  featured = false,
  sx
}: AppCardProps) {
  const dark = featured;
  return (
    <Card
      sx={{
        borderRadius: "10px",
        borderWidth: 1.5,
        borderColor: dark ? bkdp.deepNavy : bkdp.greyLight,
        backgroundColor: dark ? bkdp.deepNavy : bkdp.white,
        height: "100%",
        boxSizing: "border-box",
        ...sx
      }}
    >
      <CardContent sx={{ px: 3, py: 2.5, "&:last-child": { pb: 2.5 } }}>
        {(icon || eyebrow || action) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              mb: title || subtitle || children ? GAP : 0
            }}
            data-testid="app-card-eyebrow-row"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              {icon}
              {eyebrow && (
                <Typography
                  variant="overline"
                  sx={{ color: bkdp.signalBlue, lineHeight: 1 }}
                >
                  {eyebrow}
                </Typography>
              )}
            </Box>
            {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
          </Box>
        )}
        {title && (
          <Typography
            variant="h6"
            sx={{ color: dark ? bkdp.white : bkdp.deepNavy, mb: subtitle || children ? GAP : 0 }}
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: dark ? "rgba(255,255,255,0.72)" : bkdp.textBody,
              mb: children ? GAP : 0
            }}
          >
            {subtitle}
          </Typography>
        )}
        {children && (
          <Box sx={{ color: dark ? "rgba(255,255,255,0.72)" : bkdp.textBody }}>
            {children}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
